import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Play, FileText, X, Download, Check, Mail } from 'lucide-react';
import { formatINR } from '../../lib/format';
import { apiGet, apiPost, apiPut } from '../../lib/api';

const downloadPayslipPDF = (id) => {
  // Relative path so the link works for whichever origin is serving the
  // frontend (preview, production at elloraepc.in, on-prem, etc.) instead
  // of hard-coding the build-time REACT_APP_BACKEND_URL.
  window.open(`/api/hr/payroll/${id}/pdf`, '_blank');
};

function formatYM(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function Payroll() {
  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const [month, setMonth] = useState(formatYM(lastMonth));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [payslip, setPayslip] = useState(null);

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/hr/payroll', { month });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  const runPayroll = async () => {
    if (data?.rows?.length > 0) {
      if (!window.confirm(`Re-run payroll for ${month}? Existing records will be replaced.`)) return;
    }
    setRunning(true);
    try {
      const d = await apiPost(`/hr/payroll/run?month=${month}`, {});
      toast.success(`Payroll calculated for ${d.summary.employees} employees`);
      fetchPayroll();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error("You don't have permission to run/approve payroll. This requires HR approval access.");
      } else {
        toast.error('Payroll run failed');
      }
    } finally {
      setRunning(false);
    }
  };

  const markPaid = async (id) => {
    try {
      await apiPut(`/hr/payroll/${id}/mark-paid`, {});
      toast.success('Marked as paid');
      fetchPayroll();
    } catch {
      toast.error('Failed');
    }
  };

  const [emailingPayslips, setEmailingPayslips] = useState(false);
  const emailAllPayslips = async () => {
    if (!window.confirm(`Email payslips for ${month} to every employee with an email address?`)) return;
    setEmailingPayslips(true);
    try {
      const d = await apiPost('/hr/payroll/email-payslips', { month });
      toast.success(`Sent ${d.sent} payslips · ${d.skipped.length} skipped · ${d.failed.length} failed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to email payslips');
    } finally {
      setEmailingPayslips(false);
    }
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="payroll-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="payroll-month"
            className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          />
          <Button onClick={runPayroll} disabled={running} className="bg-[#1F7A6E] hover:bg-[#14504E]" data-testid="run-payroll-btn">
            <Play size={14} className="mr-1.5" /> {running ? 'Running…' : (rows.length > 0 ? 'Re-run' : 'Run Payroll')}
          </Button>
          {rows.length > 0 && (
            <Button onClick={emailAllPayslips} disabled={emailingPayslips} variant="outline" data-testid="email-payslips-btn">
              <Mail size={14} className="mr-1.5" /> {emailingPayslips ? 'Sending…' : 'Email All Payslips'}
            </Button>
          )}
        </div>
        {rows.length > 0 && (
          <div className="text-xs text-slate-500">
            Last run: {rows[0]?.run_at ? new Date(rows[0].run_at).toLocaleString('en-IN') : '—'} by {rows[0]?.run_by || '—'}
          </div>
        )}
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KPI label="Employees" value={summary.employees || 0} />
        <KPI label="Gross" value={formatINR(summary.total_gross)} />
        <KPI label="PF (Emp)" value={formatINR(summary.total_pf_employee || summary.total_pf)} dim />
        <KPI label="PF (Employer)" value={formatINR(summary.total_pf_employer)} dim />
        <KPI label="ESI (Emp)" value={formatINR(summary.total_esi_employee || summary.total_esi)} dim />
        <KPI label="Gratuity Prov." value={formatINR(summary.total_gratuity_provision)} dim />
        <KPI label="Net Pay" value={formatINR(summary.total_net)} accent />
        <KPI label="Employer Cost" value={formatINR(summary.total_employer_cost)} accent />
      </div>

      {(summary.esic_covered_count !== undefined) && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-2 text-xs text-slate-700 flex items-center gap-4">
          <span><strong>{summary.esic_covered_count}</strong> employees under <strong>ESIC</strong> (gross ≤ ₹21k)</span>
          <span className="text-slate-300">·</span>
          <span><strong>{summary.gpa_covered_count}</strong> under <strong>GPA + Medical Insurance</strong> (gross &gt; ₹21k)</span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5">Emp</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-center px-3 py-2.5">Days</th>
                  <th className="text-right px-3 py-2.5">Basic</th>
                  <th className="text-right px-3 py-2.5">HRA</th>
                  <th className="text-right px-3 py-2.5">Allow</th>
                  <th className="text-right px-3 py-2.5 bg-emerald-50/40">Gross</th>
                  <th className="text-right px-3 py-2.5">PF</th>
                  <th className="text-right px-3 py-2.5">ESI</th>
                  <th className="text-right px-3 py-2.5">PT</th>
                  <th className="text-right px-3 py-2.5">TDS</th>
                  <th className="text-right px-3 py-2.5 bg-teal-50/40">Net Pay</th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={14} className="text-center py-8 text-slate-500">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={14} className="text-center py-12 text-slate-500">
                    No payroll for {month}. Click <strong>Run Payroll</strong> to calculate.
                  </td></tr>
                ) : rows.map(r => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-teal-50/20" data-testid={`payroll-row-${r.emp_code}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.emp_code}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.employee_name}</div>
                      <div className="text-[11px] text-slate-500">{r.designation}</div>
                    </td>
                    <td className="px-3 py-2 text-center text-xs">
                      {r.payable_days}/{r.days_in_month}
                      {r.lop_days > 0 && <div className="text-red-500">LOP {r.lop_days}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">{formatINR(r.basic)}</td>
                    <td className="px-3 py-2 text-right">{formatINR(r.hra)}</td>
                    <td className="px-3 py-2 text-right">{formatINR(r.allowances)}</td>
                    <td className="px-3 py-2 text-right font-semibold bg-emerald-50/40">{formatINR(r.gross)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.pf)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.esi)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.pt)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.tds)}</td>
                    <td className="px-3 py-2 text-right font-bold text-[#1F7A6E] bg-teal-50/40">{formatINR(r.net_pay)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${
                        r.status === 'Paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setPayslip(r)} className="text-slate-500 hover:text-[#1F7A6E] mr-2" title="View Payslip" data-testid={`view-payslip-${r.emp_code}`}>
                        <FileText size={14} />
                      </button>
                      <button onClick={() => downloadPayslipPDF(r.id)} className="text-slate-500 hover:text-[#1F7A6E] mr-2" title="Download PDF" data-testid={`download-pdf-${r.emp_code}`}>
                        <Download size={14} />
                      </button>
                      {r.status !== 'Paid' && (
                        <button onClick={() => markPaid(r.id)} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded" title="Mark Paid" data-testid={`pay-${r.emp_code}`}>
                          <Check size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {payslip && <PayslipModal payslip={payslip} onClose={() => setPayslip(null)} />}
    </div>
  );
}

function KPI({ label, value, accent, dim }) {
  return (
    <Card className={accent ? 'border-l-4 border-l-[#1F7A6E]' : ''}>
      <CardContent className="p-3">
        <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{label}</div>
        <div className={`text-base font-bold mt-1 ${accent ? 'text-[#1F7A6E]' : dim ? 'text-slate-600' : 'text-slate-800'}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function PayslipModal({ payslip: p, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-2xl my-6" onClick={e => e.stopPropagation()} data-testid="payslip-modal">
        <div className="flex items-center justify-between border-b px-5 py-3 print:hidden">
          <h3 className="font-display font-bold text-lg">Payslip · {p.month}</h3>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => downloadPayslipPDF(p.id)} className="bg-[#1F7A6E] hover:bg-[#14504E]" data-testid="payslip-download-pdf">
              <Download size={13} className="mr-1" /> Download PDF
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6">
          <div className="text-center border-b pb-3 mb-4">
            <div className="font-display font-extrabold text-xl tracking-tight">ELR INDUSTRIES PVT LTD</div>
            <div className="text-xs text-slate-500 mt-0.5">Payslip for the month of {p.month}</div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm mb-4">
            <Row k="Employee" v={`${p.emp_code} · ${p.employee_name}`} />
            <Row k="Designation" v={p.designation} />
            <Row k="Department" v={p.department} />
            <Row k="UAN" v={p.uan} />
            <Row k="PAN" v={p.pan} />
            <Row k="Bank A/C" v={p.bank_account ? `${p.bank_account.slice(-4).padStart(p.bank_account.length, 'X')} (${p.ifsc})` : '—'} />
            <Row k="Days Payable" v={`${p.payable_days} of ${p.days_in_month}`} />
            <Row k="LOP" v={p.lop_days || 0} />
          </div>

          {/* Day-bucket breakdown — drives the proration math */}
          <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-2.5 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-[11px]" data-testid="payslip-day-buckets">
            <DayBucket label="Present" v={p.present_days} />
            <DayBucket label="Half-Day" v={p.half_days} sub="× 0.5" />
            <DayBucket label="Paid Leave" v={p.paid_leave_days} />
            <DayBucket label="LOP" v={p.lop_days} accent={(p.lop_days || 0) > 0 ? 'rose' : null} />
            <DayBucket label="Effective" v={p.effective_days ?? p.payable_days} accent="indigo" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] font-display uppercase tracking-[0.16em] text-emerald-700 mb-1 flex items-center gap-2">
                Earnings
                {(p.lop_days || 0) > 0 && p.gross_full && (
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Prorated</span>
                )}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {(() => {
                    // Prefer the full breakdown from the salary master
                    // (BASIC, DA, HRA, CA, MA, LTA, OA, …). Fall back to
                    // the legacy 3-bucket view (basic/hra/allowances) if the
                    // payroll row was generated before iter 123.
                    const comps = Array.isArray(p.components_earned) && p.components_earned.length
                      ? p.components_earned
                      : [
                          { code: 'BASIC', name: 'Basic', amount: p.basic },
                          { code: 'HRA',   name: 'HRA',   amount: p.hra },
                          { code: 'ALLOW', name: 'Allowances', amount: p.allowances },
                        ];
                    return comps.map((c, i) => (
                      <PayRow key={c.code || i} k={c.name || c.code} v={c.amount} />
                    ));
                  })()}
                  {(p.one_time_earnings || []).map((b, i) => (
                    <PayRow key={`bonus-${i}`} k={`${b.type} — ${b.note || ''}`} v={b.amount} />
                  ))}
                  <PayRow k="Gross" v={p.gross} bold />
                  {/* Employer-paid Benefits (PFs added on top of gross under
                      the bracket-based PF inclusion policy — Iter 132). */}
                  {(p.pf_inclusion_mode === 'both_above') && (
                    <PayRow k="+ PF — Employee (Employer-paid Benefit)" v={p.pf_employee || 0} />
                  )}
                  {(p.pf_inclusion_mode === 'both_above' || p.pf_inclusion_mode === 'employee_within') && (
                    <PayRow k="+ PF — Employer (Employer-paid Benefit)" v={p.pf_employer || 0} />
                  )}
                </tbody>
              </table>
              {(p.lop_days || 0) > 0 && p.gross_full && p.gross_full !== p.gross && (
                <div className="text-[10px] text-slate-500 mt-1.5 leading-snug">
                  Full gross (₹{Math.round(p.gross_full).toLocaleString('en-IN')}) prorated by{' '}
                  <strong>{p.effective_days ?? p.payable_days}/{p.days_in_month}</strong> days.
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] font-display uppercase tracking-[0.16em] text-red-700 mb-1">Deductions</div>
              <table className="w-full text-sm">
                <tbody>
                  {/* PF rows shown only when the bracket pushes them INTO gross. */}
                  {(p.pf_inclusion_mode !== 'both_above') && (
                    <PayRow k={`${pfLabel(p)} — Employee`} v={p.pf_employee ?? p.pf} />
                  )}
                  {(p.pf_inclusion_mode === 'both_within') && (
                    <PayRow k={`${pfLabel(p)} — Employer`} v={p.pf_employer || 0} />
                  )}
                  <PayRow k={`ESIC${p.coverage_type === 'ESIC' ? ' (0.75%)' : ' — N/A'}`} v={p.esi_employee ?? p.esi} />
                  <PayRow k={ptLabel(p)} v={p.pt} />
                  <PayRow k="TDS (FY 26-27 New Regime)" v={p.tds} />
                  <PayRow k="Total Deductions" v={p.deductions} bold />
                </tbody>
              </table>
              <div className="text-[10px] text-slate-500 mt-2 leading-snug">
                <div>{pfBracketNote(p)}</div>
              </div>
            </div>
          </div>

          {/* CTC summary — gratuity provision + employer ESIC + employer total cost.
              Employer PF moved into the main deductions table above per company
              policy (CTC-inclusive gross), so it's intentionally omitted here. */}
          {(p.esi_employer || p.gratuity_provision_monthly) && (
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-[10px] font-display uppercase tracking-widest text-slate-500">Employer ESIC</div>
                <div className="font-display font-bold text-slate-800">{formatINR(p.esi_employer || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] font-display uppercase tracking-widest text-slate-500">Gratuity Provision</div>
                <div className="font-display font-bold text-slate-800">{formatINR(p.gratuity_provision_monthly || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] font-display uppercase tracking-widest text-slate-500">Employer Total Cost</div>
                <div className="font-display font-bold text-[#1F7A6E]">{formatINR(p.employer_total_cost || 0)}</div>
              </div>
            </div>
          )}

          <div className="mt-5 bg-teal-50 border-l-4 border-[#1F7A6E] px-4 py-3 flex items-center justify-between">
            <div className="text-sm font-display uppercase tracking-[0.18em] text-slate-700">Net Pay</div>
            <div className="text-2xl font-extrabold text-[#1F7A6E]">{formatINR(p.net_pay)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex">
      <span className="text-slate-500 w-32">{k}</span>
      <span className="font-medium">{v || '—'}</span>
    </div>
  );
}

function PayRow({ k, v, bold }) {
  return (
    <tr className={bold ? 'border-t border-slate-200' : ''}>
      <td className={`py-1 text-slate-600 ${bold ? 'font-bold' : ''}`}>{k}</td>
      <td className={`py-1 text-right ${bold ? 'font-bold' : ''}`}>{formatINR(v)}</td>
    </tr>
  );
}

function DayBucket({ label, v, sub, accent }) {
  const colorMap = {
    rose: 'text-rose-700',
    indigo: 'text-teal-700',
  };
  return (
    <div>
      <div className="text-[10px] font-display uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-base font-bold leading-tight ${accent ? colorMap[accent] : 'text-slate-800'}`}>
        {v ?? 0}{sub && <span className="text-[10px] text-slate-400 font-normal ml-1">{sub}</span>}
      </div>
    </div>
  );
}

function pfLabel(p) {
  if (p.pf_opt_out) return 'PF (Opted out · Form 11)';
  if (p.pf_opt_out_cap) return 'PF (12% · No Cap)';
  if (p.pf_employer_capped || (p.pf_base_used && p.pf_base_used >= 15000)) return 'PF (12% · Capped @ ₹15k)';
  return 'PF (12% of Basic+DA)';
}

function ptLabel(p) {
  // Backend charges ₹300 only in February (annual ₹2500 cap)
  const isFeb = typeof p.month === 'string' && p.month.endsWith('-02');
  return isFeb ? 'Professional Tax (Feb top-up)' : 'Professional Tax';
}

// Explains the PF inclusion rule applied on this payslip, driven by the
// employee's contract gross bracket (Iter 132 policy).
function pfBracketNote(p) {
  const mode = p.pf_inclusion_mode || 'both_within';
  if (mode === 'both_above')
    return 'Gross < ₹15,000 — both Employee PF and Employer PF are employer-paid benefits ABOVE gross. Take-home reflects no PF deduction.';
  if (mode === 'employee_within')
    return 'Gross ₹15,001 – ₹21,000 — only Employee PF is netted from gross. Employer PF remains an employer-paid benefit on top.';
  return 'Gross > ₹21,000 — both Employee PF and Employer PF are netted from gross (CTC-inclusive gross model).';
}
